import { useEffect, useState } from "react";
import { Camera, MapPin, Users } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Community() {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    fetch(`${API}/community/photos`)
      .then((r) => r.json())
      .then(setPhotos)
      .catch(() => {});
  }, []);

  const wall = photos.map((p) => ({ src: `${API}/community/file/${p.storage_path}`, caption: p.caption, key: p.id }));

  return (
    <main data-testid="community-page" className="pt-32 md:pt-40 pb-24">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-xs tracking-[0.3em] uppercase text-mist mb-4">MVM — Community</p>
        <MaskedLines
          lines={["THE COMMUNITY", "IS THE BRAND."]}
          className="font-display font-bold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
        />
        <div className="mt-8 grid md:grid-cols-2 gap-8 max-w-5xl">
          <p className="text-stone/60 leading-relaxed">
            MVM was never meant to live only online. Run clubs before sunrise, training meet-ups, pop-up shops where
            you can feel the fabric and meet the people behind the mark — community isn't a marketing channel for us.
            It's the whole point.
          </p>
          <p className="text-stone/60 leading-relaxed">
            Every pop-up, every group run, every session we host is where Me vs Me stops being a slogan and becomes a
            room full of people pushing past their own walls — together. This wall is theirs.
          </p>
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-8 md:gap-14 border-y border-stone/10 py-8">
            <div className="flex items-center gap-3">
              <Users size={20} className="text-mist" strokeWidth={1.5} />
              <div>
                <p className="font-display uppercase tracking-wide">Run Clubs</p>
                <p className="text-xs text-stone/50 mt-0.5">Weekly community miles</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-mist" strokeWidth={1.5} />
              <div>
                <p className="font-display uppercase tracking-wide">Pop-Up Shops</p>
                <p className="text-xs text-stone/50 mt-0.5">Meet the brand in person</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Camera size={20} className="text-mist" strokeWidth={1.5} />
              <div>
                <p className="font-display uppercase tracking-wide">Events</p>
                <p className="text-xs text-stone/50 mt-0.5">Sessions, launches, collabs</p>
              </div>
            </div>
          </div>
        </FadeUp>

        {/* The wall */}
        <div className="mt-16 md:mt-20">
          <h2 className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mb-10">The Wall</h2>
          <div data-testid="community-wall" className="columns-2 md:columns-3 gap-4 [column-fill:balance]">
            {wall.map((p) => (
              <FadeUp key={p.key} className="mb-4 break-inside-avoid">
                <figure className="group relative overflow-hidden bg-deep">
                  <img
                    src={p.src}
                    alt={p.caption || "MVM community"}
                    loading="lazy"
                    className="w-full h-auto block transition-transform duration-[1.2s] ease-out-expo group-hover:scale-[1.05]"
                  />
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp>
          <div className="mt-20 text-center border-t border-stone/10 pt-14">
            <p className="font-display text-2xl md:text-4xl uppercase tracking-wide">
              Tag <span className="text-mist">#MVM</span> to be on the wall
            </p>
            <p className="text-stone/50 text-sm mt-3">Pop-ups, run clubs, events — show up in the kit, end up here.</p>
          </div>
        </FadeUp>
      </div>
    </main>
  );
}
